// One wrapper for a wide register table on a small screen (packhouse audit
// PW-16; the same thing farm W-18 part 3 and decision D5(a) ask for).
//
// Ten registers made a phone pan sideways with nothing pinned, so the reader
// was reading unlabelled numbers or typing into an unnamed row: the Estimates
// tonnage input is column 7 of 12, the stocktake's Counted column was cut off,
// the Security verdict sat off-screen. This wrapper:
//  - scrolls the table sideways (`overflow-x-auto`) inside `relative isolate`,
//    so a sticky cell can never paint over the page header (PW-03);
//  - PINS THE FIRST COLUMN — the identifier — while the rest pans. It needs an
//    opaque background or the panned cells show through: `stickyBg` defaults
//    to the dark shell; pass the card's colour when the table sits in a card;
//  - fades the right edge while columns are hidden, so "there is more" is seen;
//  - with `maxHeight`, owns its vertical scroll too and pins the header row.
//    ⚠️ Only then: a sticky <thead> inside an x-scroller cannot stick to the
//    PAGE, because the scroller is its containing box — farm W-18 found the
//    same, which is why a pinned first column is the rule and a pinned header
//    the exception.
import { useRef, type ReactNode } from 'react'
import clsx from 'clsx'
import { useEdgeFades } from '@/hooks/useEdgeFades'

const STICKY_FIRST = '[&_tr>*:first-child]:sticky [&_tr>*:first-child]:left-0 [&_tr>*:first-child]:z-[1]'
// A printed register is one long sheet on white paper: nothing scrolls, nothing
// pins, and a pinned cell's dark screen fill must not print (GrowersSeasonSummary).
const PRINT = 'print:overflow-visible print:[&_tr>*:first-child]:static print:[&_tr>*:first-child]:bg-transparent print:[&_thead_th]:static print:[&_thead_th]:bg-transparent'
const STICKY_HEAD = '[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-[2] [&_thead_tr>*:first-child]:z-[3]'

// Written out whole, never composed: Tailwind generates only the classes it can
// SEE in the source, so `[&_tr>*:first-child]:${bg}` would ship no CSS at all.
const SURFACE = {
  /** The dark page ground (gray-900). */
  shell: { first: '[&_tr>*:first-child]:bg-gray-900', head: '[&_thead_th]:bg-gray-900', fade: 'from-gray-900' },
  /** Inside a `card` / `surface` panel (gray-800). */
  card:  { first: '[&_tr>*:first-child]:bg-gray-800', head: '[&_thead_th]:bg-gray-800', fade: 'from-gray-800' },
  /** A printable white page. */
  light: { first: '[&_tr>*:first-child]:bg-white',    head: '[&_thead_th]:bg-white',    fade: 'from-white' },
} as const

export default function TableScroll({ children, stickyFirstCol = true, surface = 'shell', maxHeight, className }: {
  children: ReactNode
  stickyFirstCol?: boolean
  /** What the table sits on — the pinned cells need that exact opaque colour,
   *  or the panned cells show through them. */
  surface?: keyof typeof SURFACE
  /** e.g. 'max-h-[70vh]': the table owns its vertical scroll and the header row pins. */
  maxHeight?: string
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const { right: more } = useEdgeFades(ref)
  const tone = SURFACE[surface]

  return (
    <div className={clsx('relative isolate', className)}>
      <div ref={ref} data-table-scroll=""
        className={clsx(
          // relative: an sr-only / absolute child positions against the scroller,
          // not a far ancestor it would stretch sideways (farm audit W-02).
          'relative overflow-x-auto', PRINT,
          maxHeight && ['overflow-y-auto', maxHeight, STICKY_HEAD, tone.head],
          stickyFirstCol && [STICKY_FIRST, tone.first],
        )}>
        {children}
      </div>
      {more && (
        <div aria-hidden="true" className={clsx('pointer-events-none absolute inset-y-0 right-0 z-[4] w-8 bg-gradient-to-l to-transparent print:hidden', tone.fade)} />
      )}
    </div>
  )
}
